/**
 * La route `/stages/:slug` : elle récupère, puis délègue à `StageVue`.
 *
 * La vue est rendue à l'identique au build par `scripts/prerendre.mjs` — c'est
 * pour cela qu'elle vit ailleurs et ne lit rien elle-même.
 *
 * ## Un stage retiré du catalogue garde son adresse
 *
 * > « NE PAS supprimer systématiquement une page lorsqu'un stage est terminé.
 * >   La page peut conserver sa valeur SEO. »
 *
 * La vue publique ne rend plus les sessions retirées : la page dit alors que ce
 * stage n'est plus proposé, et renvoie vers ce qui l'est. Une adresse qui
 * répond vaut mieux qu'une adresse morte.
 */

import { useEffect, useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { ChevronRight, Loader2 } from 'lucide-react';
import { stageParAdresse, stagesVoisins } from '../../lib/seo/gateway.supabase';
import {
  ORGANISATEUR,
  commune,
  descriptionDuStage,
  evenementJsonLd,
  filDArianeJsonLd,
  titreDuStage,
  type StagePublic,
} from '../../lib/seo/stage';
import { ORIGINE, useEnTete } from '../../lib/seo/enTete';
import StageVue from './StageVue';

const StagePublicPage = () => {
  const { slug = '' } = useParams();
  const [stage, setStage] = useState<StagePublic | null>(null);
  const [voisins, setVoisins] = useState<StagePublic[]>([]);
  const [chargement, setChargement] = useState(true);

  useEffect(() => {
    let vivant = true;
    setChargement(true);
    stageParAdresse(slug)
      .then(async (s) => {
        if (!vivant) return;
        setStage(s);
        if (s) {
          const v = await stagesVoisins(s).catch(() => []);
          if (vivant) setVoisins(v);
        }
      })
      .catch(() => {
        if (vivant) setStage(null);
      })
      .finally(() => {
        if (vivant) setChargement(false);
      });
    return () => {
      vivant = false;
    };
  }, [slug]);

  const donnees = useMemo(() => {
    if (!stage) return undefined;
    return [
      evenementJsonLd(stage, ORIGINE),
      filDArianeJsonLd(
        [
          { nom: 'Accueil', chemin: '/' },
          { nom: 'Stages', chemin: '/stages' },
          { nom: `${stage.stage} — ${commune(stage)}`, chemin: `/stages/${stage.slug}` },
        ],
        ORIGINE,
      ),
    ];
  }, [stage]);

  useEnTete({
    titre: stage ? titreDuStage(stage) : 'Stage introuvable | Éclosion',
    description: stage
      ? descriptionDuStage(stage)
      : `Ce stage n’est plus proposé. Découvrez les prochains stages et plaines de vacances organisés par ${ORGANISATEUR} à Bruxelles.`,
    chemin: `/stages/${slug}`,
    image: stage?.image_url ?? null,
    /* Une page « plus proposé » ne mérite pas d'être indexée, mais ses liens si. */
    indexable: Boolean(stage),
    donnees,
  });

  if (chargement) {
    return (
      <div className="max-w-3xl mx-auto px-4 py-16 flex justify-center">
        <Loader2 className="h-6 w-6 animate-spin text-primary-600" />
      </div>
    );
  }

  if (!stage) {
    return (
      <div className="max-w-3xl mx-auto px-4 py-12">
        <h1 className="text-2xl font-bold text-gray-900 mb-3">
          Ce stage n’est plus proposé
        </h1>
        <p className="text-gray-600 mb-6">
          Il est possible qu’il soit terminé, ou qu’il ne soit pas encore ouvert aux
          inscriptions. {ORGANISATEUR} organise des stages et des plaines de vacances
          toute l’année à Bruxelles.
        </p>
        <Link to="/stages" className="btn-primary inline-flex items-center gap-2">
          Voir les stages actuellement proposés
          <ChevronRight className="h-4 w-4" />
        </Link>
      </div>
    );
  }

  return <StageVue stage={stage} voisins={voisins} />;
};

export default StagePublicPage;
